<template>
    <BaseForm @submit.prevent>
        <template #fields>
            <FieldGroup :title="$t('identifiers')">
                <TextField
                    :value="values.nameShort"
                    :label="$t('name-short')"
                    :error-messages="errors.nameShort"
                    :required="true"
                    @input="setFieldValue('nameShort', ($event.target as HTMLInputElement).value)"
                    @blur="setFieldTouched('nameShort', true)"
                />
                <TextField
                    :value="values.nameLong"
                    :label="$t('name-long')"
                    :error-messages="errors.nameLong"
                    :required="true"
                    @input="setFieldValue('nameLong', ($event.target as HTMLInputElement).value)"
                    @blur="setFieldTouched('nameLong', true)"
                />
                <NumberField
                    :value="values.year ?? ''"
                    :label="$t('execution-year')"
                    :error-messages="errors.year"
                    :required="true"
                    @input="setFieldValue('year', ($event.target as HTMLInputElement).value)"
                    @blur="setFieldTouched('year', true)"
                >
                </NumberField>
            </FieldGroup>
        </template>
        <template #buttons>
            <Button v-if="isTouched" size="Base" variant="Danger-Light" @click="onDiscard">
                <template #icon>
                    <UndoIcon class="size-5" />
                </template>
                {{ $t('discard') }}
            </Button>
            <Button v-else size="Base" variant="Neutral-Light" @click="$router.back">
                <template #icon>
                    <UndoIcon class="size-5" />
                </template>
                {{ $t('abort') }}
            </Button>
            <Button
                size="Base"
                variant="Info-Strong"
                :disabled-explanation="saveButtonDisabledExplanation"
                type="submit"
                @click="onSave"
            >
                <template #icon>
                    <AllDoneIcon class="size-5" />
                </template>
                {{ $t('create') }}
            </Button>
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import Button from '@/components/Button/Button.vue';
import BaseForm from '@/components/Form/BaseForm.vue';
import FieldGroup from '@/components/Form/FieldGroup.vue';
import NumberField from '@/components/Form/NumberField.vue';
import TextField from '@/components/Form/TextField.vue';
import AllDoneIcon from '@/components/Icon/AllDoneIcon.vue';
import UndoIcon from '@/components/Icon/UndoIcon.vue';
import { useForm } from '@/forms/composables/useForm';
import { computed } from 'vue';
import { useScenarioCreate } from '../composables/useScenarioCreate';
import { gotToScenario } from '../scenario';
import { ScenarioCreateSchema } from './schemas';

const props = defineProps<{ scenarioGroupId: string }>();
const scenarioGroupId = computed(() => props.scenarioGroupId);

const {
    values,
    errors,
    isTouched,
    saveButtonDisabledExplanation,
    validate,
    setFieldValue,
    setFieldTouched,
    onDiscard,
} = useForm(
    ScenarioCreateSchema,
    computed(() => ({
        nameShort: '',
        nameLong: '',
        year: '',
    })),
);

const { create } = useScenarioCreate(scenarioGroupId, values);

async function onSave() {
    const valid = await validate();
    if (valid) {
        const { id } = await create();
        if (id) {
            gotToScenario(id);
        }
    }
}
</script>
